import { useTenant } from "@/contexts/tenant-provider";

/**
 * Prefix for sandbox tenants. These use staging credentials and have
 * communication modules disabled.
 */
export const SANDBOX_PREFIX = "sandbox-";

/**
 * Prefixes that indicate a dev/test tenant.
 * Subdomains starting with these are considered non-production.
 */
const DEV_PREFIXES = ["dev", "test", "sandbox"];

/**
 * Exact subdomain matches that indicate a dev/test tenant.
 */
const DEV_SUBDOMAINS = ["demo", "dental-demo", "sandbox", "tempo", "investor"];

/**
 * Check if a subdomain matches dev tenant criteria.
 *
 * A subdomain is considered dev if:
 * - It starts with a dev prefix ("dev", "test", "sandbox"), OR
 * - It exactly matches a known dev subdomain ("demo", "dental-demo", "sandbox", "tempo", "investor")
 *
 * @param subDomain - The tenant subdomain to check
 * @returns true if the subdomain indicates a dev tenant
 */
export function isDevSubdomain(subDomain: string): boolean {
  const hasDevPrefix = DEV_PREFIXES.some((prefix) =>
    subDomain.startsWith(prefix),
  );
  return hasDevPrefix || DEV_SUBDOMAINS.includes(subDomain);
}

/**
 * Check if a subdomain is a sandbox tenant: "sandbox", "sandbox-*" or "sandbox_*".
 * Matches the backend's is_sandbox_tenant in decoda/common/tenant_utils.py.
 *
 * @param subDomain - The tenant subdomain to check
 * @returns true if the subdomain indicates a sandbox tenant
 */
export function isSandboxSubdomain(subDomain: string): boolean {
  return (
    subDomain === "sandbox" ||
    subDomain.startsWith(SANDBOX_PREFIX) ||
    subDomain.startsWith("sandbox_")
  );
}

/** Hook form of isSandboxSubdomain for the current tenant. */
export function useIsSandboxTenant(): boolean {
  const { tenant } = useTenant();
  return !!tenant?.subDomain && isSandboxSubdomain(tenant.subDomain);
}

/**
 * Hook to check if the current tenant is a dev tenant.
 * Matches the backend logic in decoda/common/tenant_utils.py (is_dev_tenant).
 *
 * @returns true if the current tenant is a dev tenant, false otherwise
 */
export function useIsDevTenant(): boolean {
  const { tenant } = useTenant();

  if (!tenant?.subDomain) {
    return false;
  }

  return isDevSubdomain(tenant.subDomain);
}
